import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { toast } from "react-hot-toast";
import { API_BASE_URL, API_BASE } from "./settings";
// import { CreateStudentRequest, CreateSchoolManagerRequest, CreateSystemUserRequest } from './types';

// Types
interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  statusCode?: number;
}

interface RefreshTokenResponse {
  token: string;
  refreshToken?: string;
  refreshTokenExpiresAt?: string;
}

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 1 minute for all requests
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem("authToken");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem("refreshToken");
        if (!refreshToken) {
          throw new Error("No refresh token available");
        }

        // Try to refresh token
        const response = await axios.post(
          `${API_BASE_URL}/accounts/refresh-token`,
          {
            expiredAccessToken: localStorage.getItem("authToken"),
            refreshToken: refreshToken,
          },
        );

        if (response.data.success && response.data.data) {
          const {
            token,
            refreshToken: newRefreshToken,
            refreshTokenExpiresAt,
          } = response.data.data as RefreshTokenResponse;

          // Update tokens
          localStorage.setItem("authToken", token);
          if (newRefreshToken) {
            localStorage.setItem("refreshToken", newRefreshToken);
          }
          if (refreshTokenExpiresAt) {
            localStorage.setItem(
              "refreshTokenExpiresAt",
              refreshTokenExpiresAt,
            );
          }

          // Update authorization header
          originalRequest.headers.Authorization = `Bearer ${token}`;

          // Retry original request
          return api(originalRequest);
        } else {
          throw new Error("Token refresh failed");
        }
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);

        // Clear tokens
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("refreshTokenExpiresAt");
        localStorage.removeItem("userId");

        // Show error toast
        toast.error("Oturum süresi dolmuş. Lütfen tekrar giriş yapın.");

        // Redirect to login (will be handled by router)
        window.location.href = "/login";

        return Promise.reject(refreshError);
      }
    }

    // Handle other errors
    let errorMessage = "Bir hata oluştu";

    if (error.response?.data) {
      const errorData = error.response.data;

      // Handle new API error format
      if (errorData.Detail) {
        errorMessage = errorData.Detail;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.Title) {
        errorMessage = errorData.Title;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    // Show toast for all errors (including login errors)
    toast.error(errorMessage);

    return Promise.reject(error);
  },
);

// API methods
export const authAPI = {
  login: async (credentials: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/accounts/login", credentials);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  register: async (userData: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/accounts/register", userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  refreshToken: async (request: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/accounts/refresh-token", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  changePassword: async (request: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/accounts/change-password", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  forgotPassword: async (request: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/accounts/forgot-password", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  resetPassword: async (request: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/accounts/reset-password", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Admin Login
  adminLogin: async (credentials: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/accounts/admin/login", credentials);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Admin Change Password
  adminChangePassword: async (data: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/accounts/admin/change-password", data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

export const locationsAPI = {
  getCities: async (): Promise<ApiResponse> => {
    try {
      const response = await api.get("/locations/cities");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getDistricts: async (cityId: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/locations/districts?cityId=${cityId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getNeighborhoods: async (districtId: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(
        `/locations/neighborhoods?districtId=${districtId}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // School Managers
  listSchoolManagers: async (params: {
    Q?: string;
    Page: number;
    Size: number;
    OrderBy: number;
    SortBy?: string;
  }): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/education/school-managers?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getSchoolManagerDetails: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/education/school-managers/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateSchoolManager: async (id: string, data: any): Promise<ApiResponse> => {
    try {
      const response = await api.put(`/education/school-managers/${id}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteSchoolManager: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.delete(`/education/school-managers/${id}`);

      // .NET MediatR Unit.Value response handling
      // Unit.Value genellikle boş response döner veya 204 No Content
      if (response.status === 204 || response.status === 200) {
        return {
          success: true,
          message: "Okul müdürü başarıyla silindi",
          data: null,
        };
      }

      // Eğer response.data varsa onu kullan
      return (
        response.data || {
          success: true,
          message: "Okul müdürü başarıyla silindi",
          data: null,
        }
      );
    } catch (error: any) {
      // 404 Not Found handling
      if (error.response?.status === 404) {
        const notFoundError = new Error("Okul müdürü bulunamadı");
        (notFoundError as any).response = {
          ...error.response,
          data: {
            success: false,
            message: "Okul müdürü bulunamadı",
            data: null,
          },
        };
        throw notFoundError;
      }

      // Diğer hatalar için genel işlem
      if (error.response?.data) {
        const apiError = new Error(
          error.response.data.message || "Okul müdürü silinirken hata oluştu",
        );
        (apiError as any).response = {
          ...error.response,
          data: {
            success: false,
            message:
              error.response.data.message ||
              "Okul müdürü silinirken hata oluştu",
            data: null,
          },
        };
        throw apiError;
      }

      throw error;
    }
  },

  // Account Management
  login: async (credentials: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/accounts/login", credentials);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  forgotPassword: async (request: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/accounts/forgot-password", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  resetPassword: async (request: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/accounts/reset-password", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  verifyOtp: async (request: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/accounts/verify-otp", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

export const educationAPI = {
  // School Brands
  createSchoolBrand: async (request: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/education/school-brands", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  listSchoolBrands: async (params: any): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/education/school-brands?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getSchoolBrandDetails: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/education/school-brands/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateSchoolBrand: async (id: string, request: any): Promise<ApiResponse> => {
    try {
      const response = await api.put(`/education/school-brands/${id}`, request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteSchoolBrand: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.delete(`/education/school-brands/${id}`);

      // .NET MediatR Unit.Value response handling
      // Unit.Value genellikle boş response döner veya 204 No Content
      if (response.status === 204 || response.status === 200) {
        return {
          success: true,
          message: "Marka başarıyla silindi",
          data: null,
        };
      }

      // Eğer response.data varsa onu kullan
      return (
        response.data || {
          success: true,
          message: "Marka başarıyla silindi",
          data: null,
        }
      );
    } catch (error: any) {
      // 404 Not Found handling
      if (error.response?.status === 404) {
        const notFoundError = new Error("Marka bulunamadı");
        (notFoundError as any).response = {
          ...error.response,
          data: {
            success: false,
            message: "Marka bulunamadı",
            data: null,
          },
        };
        throw notFoundError;
      }

      // Diğer hatalar için genel işlem
      if (error.response?.data) {
        const apiError = new Error(
          error.response.data.message || "Marka silinirken hata oluştu",
        );
        (apiError as any).response = {
          ...error.response,
          data: {
            success: false,
            message:
              error.response.data.message || "Marka silinirken hata oluştu",
            data: null,
          },
        };
        throw apiError;
      }

      throw error;
    }
  },

  // Schools
  createSchool: async (request: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/education/schools", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  listSchools: async (params: any): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/education/schools?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getSchoolDetails: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/education/schools/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateSchool: async (id: string, request: any): Promise<ApiResponse> => {
    try {
      const response = await api.put(`/education/schools/${id}`, request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteSchool: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.delete(`/education/schools/${id}`);

      // .NET MediatR Unit.Value response handling
      // Unit.Value genellikle boş response döner veya 204 No Content
      if (response.status === 204 || response.status === 200) {
        return {
          success: true,
          message: "Okul başarıyla silindi",
          data: null,
        };
      }

      // Eğer response.data varsa onu kullan
      return (
        response.data || {
          success: true,
          message: "Okul başarıyla silindi",
          data: null,
        }
      );
    } catch (error: any) {
      // 404 Not Found handling
      if (error.response?.status === 404) {
        const notFoundError = new Error("Okul bulunamadı");
        (notFoundError as any).response = {
          ...error.response,
          data: {
            success: false,
            message: "Okul bulunamadı",
            data: null,
          },
        };
        throw notFoundError;
      }

      // Diğer hatalar için genel işlem
      if (error.response?.data) {
        const apiError = new Error(
          error.response.data.message || "Okul silinirken hata oluştu",
        );
        (apiError as any).response = {
          ...error.response,
          data: {
            success: false,
            message:
              error.response.data.message || "Okul silinirken hata oluştu",
            data: null,
          },
        };
        throw apiError;
      }

      throw error;
    }
  },

  // Students
  createStudent: async (request: any): Promise<ApiResponse> => {
    try {
      // Use v2 endpoint with full URL (base URL has /api/v1, so we need to use API_BASE)
      const response = await api.post(
        `${API_BASE}/api/v2/education/students`,
        request,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getStudents: async (params: any): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      // GradeSectionFilters parametresini ekle
      if (params.GradeSectionFilters && params.GradeSectionFilters.length > 0) {
        queryParams.append(
          "GradeSectionFilters",
          JSON.stringify(params.GradeSectionFilters),
        );
      }

      const response = await api.get(
        `/education/students?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteStudent: async (studentId: string): Promise<ApiResponse> => {
    try {
      const response = await api.delete(`/education/students/${studentId}`);

      // Handle 204 No Content response
      if (response.status === 204) {
        return {
          success: true,
          message: "Öğrenci başarıyla silindi",
          data: null,
        };
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getStudentDetails: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/education/students/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateStudent: async (id: string, request: any): Promise<ApiResponse> => {
    try {
      // Use v2 endpoint with full URL (base URL has /api/v1, so we need to use API_BASE)
      const response = await api.put(
        `${API_BASE}/api/v2/education/students/${id}`,
        request,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Classroom API functions
  getClassrooms: async (params: any): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/education/classrooms?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createClassroom: async (request: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/education/classrooms", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getClassroomDetails: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/education/classrooms/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  getEventDocuments: async (eventId: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/education/events/${eventId}/documents`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  updateClassroom: async (id: string, request: any): Promise<ApiResponse> => {
    try {
      const response = await api.put(`/education/classrooms/${id}`, request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteClassroom: async (classroomId: string): Promise<ApiResponse> => {
    try {
      const response = await api.delete(`/education/classrooms/${classroomId}`);

      // Handle 204 No Content response
      if (response.status === 204) {
        return {
          success: true,
          message: "Sınıf başarıyla silindi",
          data: null,
        };
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // School Managers
  createSchoolManager: async (request: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/education/school-managers", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // School Users
  createSchoolUser: async (request: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/education/school-users", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateSchoolUser: async (id: string, data: any): Promise<ApiResponse> => {
    try {
      const response = await api.put(`/education/school-users/${id}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getSchoolUserDetails: async (
      id: string,
    ): Promise<ApiResponse> => {
      try {
        const response = await api.get(`/education/school-users/${id}`);
        return response.data;
      } catch (error) {
        throw error;
      }
  },
  deleteSchoolUser: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.delete(`/education/school-users/${id}`);

      // .NET MediatR Unit.Value response handling
      // Unit.Value genellikle boş response döner veya 204 No Content
      if (response.status === 204 || response.status === 200) {
        return {
          success: true,
          message: "Okul kullanıcısı başarıyla silindi",
          data: null,
        };
      }

      // Eğer response.data varsa onu kullan
      return (
        response.data || {
          success: true,
          message: "Okul kullanıcısı başarıyla silindi",
          data: null,
        }
      );
    } catch (error: any) {
      // 404 Not Found handling
      if (error.response?.status === 404) {
        const notFoundError = new Error("Okul kullanıcısı bulunamadı");
        (notFoundError as any).response = {
          ...error.response,
          data: {
            success: false,
            message: "Okul kullanıcısı bulunamadı",
            data: null,
          },
        };
        throw notFoundError;
      }

      // Diğer hatalar için genel işlem
      if (error.response?.data) {
        const apiError = new Error(
          error.response.data.message ||
            "Okul kullanıcısı silinirken hata oluştu",
        );
        (apiError as any).response = {
          ...error.response,
          data: {
            success: false,
            message:
              error.response.data.message ||
              "Okul kullanıcısı silinirken hata oluştu",
            data: null,
          },
        };
        throw apiError;
      }

      throw error;
    }
  },

  listSchoolManagers: async (params: {
    Q?: string;
    Page: number;
    Size: number;
    OrderBy: number;
    SortBy?: string;
  }): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/education/school-managers?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getSchoolManagerDetails: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/education/school-managers/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateSchoolManager: async (id: string, data: any): Promise<ApiResponse> => {
    try {
      const response = await api.put(`/education/school-managers/${id}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteSchoolManager: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.delete(`/education/school-managers/${id}`);

      // .NET MediatR Unit.Value response handling
      // Unit.Value genellikle boş response döner veya 204 No Content
      if (response.status === 204 || response.status === 200) {
        return {
          success: true,
          message: "Okul müdürü başarıyla silindi",
          data: null,
        };
      }

      // Eğer response.data varsa onu kullan
      return (
        response.data || {
          success: true,
          message: "Okul müdürü başarıyla silindi",
          data: null,
        }
      );
    } catch (error: any) {
      // 404 Not Found handling
      if (error.response?.status === 404) {
        const notFoundError = new Error("Okul müdürü bulunamadı");
        (notFoundError as any).response = {
          ...error.response,
          data: {
            success: false,
            message: "Okul müdürü bulunamadı",
            data: null,
          },
        };
        throw notFoundError;
      }

      // Diğer hatalar için genel işlem
      if (error.response?.data) {
        const apiError = new Error(
          error.response.data.message || "Okul müdürü silinirken hata oluştu",
        );
        (apiError as any).response = {
          ...error.response,
          data: {
            success: false,
            message:
              error.response.data.message ||
              "Okul müdürü silinirken hata oluştu",
            data: null,
          },
        };
        throw apiError;
      }

      throw error;
    }
  },

  // School Users (Me)
  listSchoolUsers: async (params: {
    Q?: string;
    Page: number;
    Size: number;
    OrderBy: number;
    SortBy?: string;
  }): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/education/school-users/all?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // System Users
  getSystemUsers: async (params: any): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);
      if (params.Status !== undefined)
        queryParams.append("Status", params.Status.toString());
      if (params.UserTypes && Array.isArray(params.UserTypes)) {
        params.UserTypes.forEach((userType: string) => {
          queryParams.append("UserTypes", userType);
        });
      }

      const response = await api.get(`/users?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createSystemUser: async (request: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/accounts/register", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getSystemUserDetails: async (userId: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/users/${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateSystemUser: async (userId: string, data: any): Promise<ApiResponse> => {
    try {
      const response = await api.put(`/application-users/${userId}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteSystemUser: async (userId: string): Promise<ApiResponse> => {
    try {
      const response = await api.delete(`/users/${userId}`);

      // .NET MediatR Unit.Value response handling
      // Unit.Value genellikle boş response döner veya 204 No Content
      if (response.status === 204 || response.status === 200) {
        return {
          success: true,
          message: "Kullanıcı başarıyla silindi",
          data: null,
        };
      }

      // Eğer response.data varsa onu kullan
      return (
        response.data || {
          success: true,
          message: "Kullanıcı başarıyla silindi",
          data: null,
        }
      );
    } catch (error: any) {
      // 404 Not Found handling
      if (error.response?.status === 404) {
        const notFoundError = new Error("Kullanıcı bulunamadı");
        (notFoundError as any).response = {
          ...error.response,
          data: {
            success: false,
            message: "Kullanıcı bulunamadı",
            data: null,
          },
        };
        throw notFoundError;
      }

      // Diğer hatalar için genel işlem
      if (error.response?.data) {
        const apiError = new Error(
          error.response.data.message || "Kullanıcı silinirken hata oluştu",
        );
        (apiError as any).response = {
          ...error.response,
          data: {
            success: false,
            message:
              error.response.data.message || "Kullanıcı silinirken hata oluştu",
            data: null,
          },
        };
        throw apiError;
      }

      throw error;
    }
  },

  // Account Management
  login: async (credentials: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/accounts/login", credentials);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  forgotPassword: async (request: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/accounts/forgot-password", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  resetPassword: async (request: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/accounts/reset-password", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  verifyOtp: async (request: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/accounts/verify-otp", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // System Admins
  listSystemAdmins: async (params: any): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());

      const response = await api.get(
        `/education/application-users?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getSystemAdminDetails: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/education/application-users/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateSystemAdmin: async (id: string, data: any): Promise<ApiResponse> => {
    try {
      const response = await api.put(
        `/education/application-users/${id}`,
        data,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteSystemAdmin: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.delete(`/education/application-users/${id}`);

      // .NET MediatR Unit.Value response handling
      // Unit.Value genellikle boş response döner veya 204 No Content
      if (response.status === 204 || response.status === 200) {
        return {
          success: true,
          message: "Sistem yöneticisi başarıyla silindi",
          data: null,
        };
      }

      // Eğer response.data varsa onu kullan
      return (
        response.data || {
          success: true,
          message: "Sistem yöneticisi başarıyla silindi",
          data: null,
        }
      );
    } catch (error: any) {
      // 404 Not Found handling
      if (error.response?.status === 404) {
        const notFoundError = new Error("Sistem yöneticisi bulunamadı");
        (notFoundError as any).response = {
          ...error.response,
          data: {
            success: false,
            message: "Sistem yöneticisi bulunamadı",
            data: null,
          },
        };
        throw notFoundError;
      }

      // Diğer hatalar için genel işlem
      if (error.response?.data) {
        const apiError = new Error(
          error.response.data.message ||
            "Sistem yöneticisi silinirken hata oluştu",
        );
        (apiError as any).response = {
          ...error.response,
          data: {
            success: false,
            message:
              error.response.data.message ||
              "Sistem yöneticisi silinirken hata oluştu",
            data: null,
          },
        };
        throw apiError;
      }

      throw error;
    }
  },

  createSystemAdmin: async (data: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/application-users", data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Parents
  createParent: async (request: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/education/parents", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  listParents: async (params: {
    Q?: string;
    Page: number;
    Size: number;
    OrderBy: number;
    SortBy?: string;
  }): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/education/parents?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getParentDetails: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/education/parents/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateParent: async (id: string, data: any): Promise<ApiResponse> => {
    try {
      const response = await api.put(`/education/parents/${id}`, data);

      // Handle 204 No Content response
      if (response.status === 204) {
        return {
          success: true,
          message: "Veli başarıyla güncellendi",
          data: null,
        };
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteParent: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.delete(`/education/parents/${id}`);

      // Handle 204 No Content response
      if (response.status === 204) {
        return {
          success: true,
          message: "Veli başarıyla silindi",
          data: null,
        };
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  checkParent: async (
    phoneCode: string,
    phoneNumber: string,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.post("/education/parents/check", {
        phoneCode,
        phoneNumber,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  addParent: async (
    userId: string,
    identityNumber: string,
    occupation: string,
    birthDate: string | null,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.post("/education/parents/add", {
        userId,
        identityNumber,
        occupation,
        birthDate: birthDate || null,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Student-Parent Management
  addParentToStudent: async (
    studentId: string,
    parentId: string,
    parentRelationType: number,
    isPaymentManager: boolean = false,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        `/education/students/${studentId}/parents`,
        {
          id: studentId,
          parentId: parentId,
          parentRelationType: parentRelationType,
          isPaymentManager: isPaymentManager,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateStudentParent: async (
    studentId: string,
    parentId: string,
    parentRelationType: number,
    isPaymentManager: boolean,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.put(
        `/education/students/${studentId}/parents/${parentId}`,
        {
          parentRelationType: parentRelationType,
          isPaymentManager: isPaymentManager,
        },
      );

      // Handle 204 No Content response
      if (response.status === 204) {
        return {
          success: true,
          message: "Veli-öğrenci ilişkisi başarıyla güncellendi",
          data: null,
        };
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Set payment manager for student (immediate update)
  setStudentPaymentManager: async (
    studentId: string,
    parentId: string,
    relationType: number,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.put(
        `/education/students/${studentId}/parents`,
        {
          parentId: parentId,
          parentRelationType: relationType,
          isPaymentManager: true,
        },
      );

      // Handle 204 No Content response
      if (response.status === 204) {
        return {
          success: true,
          message: "Ödeme yöneticisi başarıyla güncellendi",
          data: null,
        };
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  removeParentFromStudent: async (
    studentId: string,
    parentId: string,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.delete(
        `/education/students/${studentId}/parents/${parentId}`,
      );

      // Handle 204 No Content response
      if (response.status === 204) {
        return {
          success: true,
          message: "Veli-öğrenci ilişkisi başarıyla kaldırıldı",
          data: null,
        };
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Events
  createEvent: async (request: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/education/events", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  listEvents: async (params: {
    Q?: string;
    Page: number;
    Size: number;
    OrderBy: number;
    SortBy?: string;
  }): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/education/events?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getEventDetails: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/education/events/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateEvent: async (id: string, request: any): Promise<ApiResponse> => {
    try {
      const response = await api.put(`/education/events/${id}`, request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteEvent: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.delete(`/education/events/${id}`);

      // .NET MediatR Unit.Value response handling
      if (response.status === 204 || response.status === 200) {
        return {
          success: true,
          message: "Etkinlik başarıyla silindi",
          data: null,
        };
      }

      return (
        response.data || {
          success: true,
          message: "Etkinlik başarıyla silindi",
          data: null,
        }
      );
    } catch (error: any) {
      // 404 Not Found handling
      if (error.response?.status === 404) {
        const notFoundError = new Error("Etkinlik bulunamadı");
        (notFoundError as any).response = {
          ...error.response,
          data: {
            success: false,
            message: "Etkinlik bulunamadı",
            data: null,
          },
        };
        throw notFoundError;
      }

      // Diğer hatalar için genel işlem
      if (error.response?.data) {
        const apiError = new Error(
          error.response.data.message || "Etkinlik silinirken hata oluştu",
        );
        (apiError as any).response = {
          ...error.response,
          data: {
            success: false,
            message:
              error.response.data.message || "Etkinlik silinirken hata oluştu",
            data: null,
          },
        };
        throw apiError;
      }

      throw error;
    }
  },

  // Event Registrations
  bulkAddStudentsToEvent: async (
    eventId: string,
    studentIds: string[],
  ): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        `/education/events/${eventId}/registrations/sync`,
        {
          id: eventId,
          studentIds: studentIds,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Add single student to event
  addStudentToEvent: async (
    eventId: string,
    studentId: string,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        `/education/events/${eventId}/registrations`,
        {
          id: eventId,
          studentId: studentId,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Remove all registrations from event
  removeAllEventRegistrations: async (
    eventId: string,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.delete(
        `/education/events/${eventId}/registrations/sync`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Approve event registration
  approveEventRegistration: async (
    eventId: string,
    studentEventId: string,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        `/education/events/${eventId}/registrations/approval`,
        {
          id: eventId,
          studentEventId: studentEventId,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Cancel event registrations
  cancelEventRegistrations: async (
    eventId: string,
    studentEventIds: string[],
  ): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        `/education/events/${eventId}/registrations/cancel`,
        {
          id: eventId,
          studentEventIds: studentEventIds,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get extended event details
  getEventExtendedDetails: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/education/events/detail/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Send event reminder
  sendEventReminder: async (request: {
    id: string;
    eventStudentIds: string[];
    sms: boolean;
    push: boolean;
    email: boolean;
    isAllUser: boolean;
  }): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        "/education/events/send-reminder",
        request,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get event students with pagination
  getEventStudents: async (
    eventId: string,
    params: {
      Q?: string;
      Page: number;
      Size: number;
      OrderBy: number;
      SortBy?: string;
    },
  ): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/education/events/${eventId}/students?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get students by classroom
  getClassroomStudents: async (classroomId: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(
        `/education/classrooms/${classroomId}/students`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Add student to school
  addStudentToSchool: async (
    schoolId: string,
    studentId: string,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        `/education/schools/${schoolId}/students/${studentId}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get my events (school manager's events)
  getMyEvents: async (params: {
    Q?: string;
    Page: number;
    Size: number;
    OrderBy: number;
    SortBy?: string;
  }): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/education/events/me?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get my school managers
  getMySchoolManagers: async (params: {
    Q?: string;
    Page: number;
    Size: number;
    OrderBy: number;
    SortBy?: string;
  }): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/education/school-managers/me?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get parent's students
  getParentStudents: async (parentId: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/education/parents/${parentId}/students`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  uploadDocument: async (
    eventId: string,
    file: File,
    title?: string,
  ): Promise<ApiResponse> => {
    try {
      const formData = new FormData();
      formData.append("Name", file.name);
      formData.append("Title", title || file.name);
      formData.append("Document", file);

      const response = await api.post(
        `/education/events/${eventId}/documents`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteDocuments: async (
    eventId: string,
    documentIds: string[],
  ): Promise<ApiResponse> => {
    try {
      // Her dokuman için ayrı ayrı DELETE isteği gönder
      const deletePromises = documentIds.map((documentId) =>
        api.delete(`/education/events/${eventId}/documents/${documentId}`),
      );

      await Promise.all(deletePromises);

      return {
        success: true,
        message: `${documentIds.length} dokuman başarıyla silindi`,
        data: null,
      };
    } catch (error) {
      throw error;
    }
  },

  deleteDocument: async (
    eventId: string,
    documentId: string,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.delete(
        `/education/events/${eventId}/documents/${documentId}`,
      );

      // .NET MediatR Unit.Value response handling
      if (response.status === 204 || response.status === 200) {
        return {
          success: true,
          message: "Dokuman başarıyla silindi",
          data: null,
        };
      }

      return (
        response.data || {
          success: true,
          message: "Dokuman başarıyla silindi",
          data: null,
        }
      );
    } catch (error: any) {
      // 404 Not Found handling
      if (error.response?.status === 404) {
        const notFoundError = new Error("Dokuman bulunamadı");
        (notFoundError as any).response = {
          ...error.response,
          data: {
            success: false,
            message: "Dokuman bulunamadı",
            data: null,
          },
        };
        throw notFoundError;
      }

      // Diğer hatalar için genel işlem
      if (error.response?.data) {
        const apiError = new Error(
          error.response.data.message || "Dokuman silinirken hata oluştu",
        );
        (apiError as any).response = {
          ...error.response,
          data: {
            success: false,
            message:
              error.response.data.message || "Dokuman silinirken hata oluştu",
            data: null,
          },
        };
        throw apiError;
      }

      throw error;
    }
  },
};

// Finance API
export const financeAPI = {
  // Get receivables for admin (school payments list)
  getReceivablesAdmin: async (params: {
    Q?: string;
    Page: number;
    Size: number;
    OrderBy: number;
    SortBy?: string;
  }): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/finance/receivables/admin?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getEventReceivables: async (
    eventId: string,
    params: {
      Page: number;
      Size: number;
      OrderBy: number;
    },
  ): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("EventId", eventId);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());

      const response = await api.get(
        `/finance/payments/receivables?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // List all receivables with filters
  listReceivables: async (params: {
    EventId?: string;
    StatusId?: number;
    Q?: string;
    Page: number;
    Size: number;
    OrderBy: number;
    SortBy?: string;
  }): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.EventId) queryParams.append("EventId", params.EventId);
      if (params.StatusId !== undefined)
        queryParams.append("StatusId", params.StatusId.toString());
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/finance/receivables?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get upcoming receivables
  getUpcomingReceivables: async (params: {
    StudentId?: string;
    EventId?: string;
    SchoolId?: string;
    StartsAt?: string;
    EndsAt?: string;
    LastPaymentDate?: string;
    OnlyOpen?: boolean;
    StatusId?: number;
    Q?: string;
    Page: number;
    Size: number;
    OrderBy: number;
    SortBy?: string;
  }): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.StudentId) queryParams.append("StudentId", params.StudentId);
      if (params.EventId) queryParams.append("EventId", params.EventId);
      if (params.SchoolId) queryParams.append("SchoolId", params.SchoolId);
      if (params.StartsAt) queryParams.append("StartsAt", params.StartsAt);
      if (params.EndsAt) queryParams.append("EndsAt", params.EndsAt);
      if (params.LastPaymentDate)
        queryParams.append("LastPaymentDate", params.LastPaymentDate);
      if (params.OnlyOpen !== undefined)
        queryParams.append("OnlyOpen", params.OnlyOpen.toString());
      if (params.StatusId !== undefined)
        queryParams.append("StatusId", params.StatusId.toString());
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/finance/receivables/upcoming?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get past receivables
  getPastReceivables: async (params: {
    StudentId?: string;
    EventId?: string;
    SchoolId?: string;
    DueFrom?: string;
    DueTo?: string;
    OnlyClosed?: boolean;
    OnlyOpen?: boolean;
    Q?: string;
    Page: number;
    Size: number;
    OrderBy: number;
    SortBy?: string;
  }): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.StudentId) queryParams.append("StudentId", params.StudentId);
      if (params.EventId) queryParams.append("EventId", params.EventId);
      if (params.SchoolId) queryParams.append("SchoolId", params.SchoolId);
      if (params.DueFrom) queryParams.append("DueFrom", params.DueFrom);
      if (params.DueTo) queryParams.append("DueTo", params.DueTo);
      if (params.OnlyClosed !== undefined)
        queryParams.append("OnlyClosed", params.OnlyClosed.toString());
      if (params.OnlyOpen !== undefined)
        queryParams.append("OnlyOpen", params.OnlyOpen.toString());
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/finance/receivables/past?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Sync receivables for an event
  syncReceivables: async (eventId: string): Promise<ApiResponse> => {
    try {
      const response = await api.post("/finance/receivables/sync", {
        eventId: eventId,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  createReceivable: async (request: {
    name: string;
    period: string;
    type: number;
    paymentType: number;
    currency: number;
    lastPaymentDate: string;
    description: string;
    students: Array<{ studentId: string; price: number }>;
  }): Promise<ApiResponse> => {
    try {
      const response = await api.post("/finance/receivables/create", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  syncEventReceivables: async (eventId: string): Promise<ApiResponse> => {
    try {
      const response = await api.post("/finance/receivables/sync", {
        eventId: eventId,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  sendReminder: async (request: {
    id: string;
    receivableId: string[];
    sms: boolean;
    push: boolean;
    email: boolean;
    isAllUser: boolean;
  }): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        "/finance/receivables/send-reminder",
        request,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getReceivableDetails: async (receivableId: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(
        `/finance/payments/receivables/${receivableId}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get school receivable detail (school payments)
  getSchoolReceivableDetail: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/finance/receivables/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Send reminder for school receivables
  sendSchoolReceivableReminder: async (request: {
    id: string;
    receivableId: string[];
    sms: boolean;
    push: boolean;
    email: boolean;
    isAllUser: boolean;
  }): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        "/finance/receivables/send-reminder",
        request,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Get current user information
export const getCurrentUser = async (): Promise<ApiResponse> => {
  try {
    const response = await api.get("/users/me");
    return response.data;
  } catch (error: any) {
    console.error("Error fetching current user:", error);
    if (error.response?.status === 401) {
      // Token expired, redirect to login
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("refreshTokenExpiresAt");
      localStorage.removeItem("userId");
      window.location.href = "/login";
    }
    throw error;
  }
};

// Get dashboard statistics
export const getDashboardStats = async (): Promise<ApiResponse> => {
  try {
    const response = await api.get("/users/dashboard");
    return response.data;
  } catch (error: any) {
    console.error("Error fetching dashboard stats:", error);
    throw error;
  }
};

// Change password
export const changePassword = async (
  password: string,
  newPassword: string,
): Promise<ApiResponse> => {
  try {
    const response = await api.post("/accounts/change-password", {
      password,
      newPassword,
    });
    return response.data;
  } catch (error: any) {
    console.error("Error changing password:", error);
    if (error.response?.status === 401) {
      // Token expired, redirect to login
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("refreshTokenExpiresAt");
      localStorage.removeItem("userId");
      window.location.href = "/login";
    }
    throw error;
  }
};

// Get receivables
export const getReceivables = async (params: {
  Q?: string;
  Page: number;
  Size: number;
  OrderBy: number;
}): Promise<ApiResponse> => {
  try {
    const response = await api.get("/finance/payments/receivables", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// School Users


// Notifications API
export const notificationsAPI = {
  // OTP
  sendOtp: async (request: {
    email?: string;
    phoneCode?: string;
    phoneNumber?: string;
  }): Promise<ApiResponse> => {
    try {
      const response = await api.post("/notifications/otp/send", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  verifyOtp: async (request: {
    email?: string;
    phoneCode?: string;
    phoneNumber?: string;
    code: string;
  }): Promise<ApiResponse> => {
    try {
      const response = await api.post("/notifications/otp/verify", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Push Notifications
  sendPush: async (request: {
    title: string;
    body: string;
    userIds: string[];
  }): Promise<ApiResponse> => {
    try {
      const response = await api.post("/notifications/push/send", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  listPushNotifications: async (params: {
    UserId?: string;
    Status?: number;
    From?: string;
    To?: string;
    Q?: string;
    Page: number;
    Size: number;
    OrderBy: number;
    SortBy?: string;
  }): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.UserId) queryParams.append("UserId", params.UserId);
      if (params.Status !== undefined)
        queryParams.append("Status", params.Status.toString());
      if (params.From) queryParams.append("From", params.From);
      if (params.To) queryParams.append("To", params.To);
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/notifications/push?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Push Subscriptions
  upsertPushSubscription: async (request: {
    playerId: string;
    deviceId: string;
  }): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        "/notifications/push-subscriptions/upsert",
        request,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Me API
export const meAPI = {
  // Notifications
  getMyNotifications: async (params: {
    Status?: number;
    OnlyUnread?: boolean;
    Since?: string;
    Q?: string;
    Page: number;
    Size: number;
    OrderBy: number;
    SortBy?: string;
  }): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Status !== undefined)
        queryParams.append("Status", params.Status.toString());
      if (params.OnlyUnread !== undefined)
        queryParams.append("OnlyUnread", params.OnlyUnread.toString());
      if (params.Since) queryParams.append("Since", params.Since);
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/me/notifications?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  markNotificationAsRead: async (
    notificationId: string,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        `/me/notifications/${notificationId}/mark-as-read`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  markAllNotificationsAsRead: async (): Promise<ApiResponse> => {
    try {
      const response = await api.post("/me/notifications/mark-all-as-read");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Notification Settings
  updateNotificationSettings: async (request: {
    inAppEnabled?: boolean;
    pushEnabled?: boolean;
    emailEnabled?: boolean;
    smsEnabled?: boolean;
    quietHoursStart?: string;
    quietHoursEnd?: string;
    timezone?: string;
    mutedCategoriesJson?: string;
  }): Promise<ApiResponse> => {
    try {
      const response = await api.put("/me/notification-settings", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Agreements API
export const agreementsAPI = {
  // Create Agreement (multipart/form-data)
  createAgreement: async (request: {
    name: string;
    agreementType: number;
    version: string;
    title: string;
    document: File;
    isForceUpdate?: boolean;
  }): Promise<ApiResponse> => {
    try {
      const formData = new FormData();
      formData.append("Name", request.name);
      formData.append("AgreementType", request.agreementType.toString());
      formData.append("Version", request.version);
      formData.append("Title", request.title);
      formData.append("Document", request.document);
      if (request.isForceUpdate !== undefined) {
        formData.append("IsForceUpdate", request.isForceUpdate.toString());
      }

      const response = await api.post("/agreements", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  listAgreements: async (params: {
    Q?: string;
    Page: number;
    Size: number;
    OrderBy: number;
    SortBy?: string;
  }): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(`/agreements?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getAgreementDetails: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/agreements/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateAgreement: async (
    id: string,
    request: {
      name?: string;
      agreementType?: number;
      newVersion?: string;
      title?: string;
      content?: string;
      isForceUpdate?: boolean;
    },
  ): Promise<ApiResponse> => {
    try {
      const formData = new FormData();
      formData.append("Id", id);
      if (request.name) formData.append("Name", request.name);
      if (request.agreementType !== undefined)
        formData.append("AgreementType", request.agreementType.toString());
      if (request.newVersion) formData.append("NewVersion", request.newVersion);
      if (request.title) formData.append("Title", request.title);
      if (request.content) formData.append("Content", request.content);
      if (request.isForceUpdate !== undefined)
        formData.append("IsForceUpdate", request.isForceUpdate.toString());

      const response = await api.put(`/agreements/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteAgreement: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.delete(`/agreements/${id}`);
      if (response.status === 204 || response.status === 200) {
        return {
          success: true,
          message: "Sözleşme başarıyla silindi",
          data: null,
        };
      }
      return (
        response.data || {
          success: true,
          message: "Sözleşme başarıyla silindi",
          data: null,
        }
      );
    } catch (error) {
      throw error;
    }
  },

  // Assignments
  assignAgreementToUser: async (
    agreementId: string,
    userId: string,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        `/agreements/${agreementId}/assignments/${userId}`,
        {
          id: agreementId,
          userId: userId,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  assignAgreementToEvent: async (
    agreementId: string,
    eventId: string,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        `/agreements/${agreementId}/assignments/events/${eventId}`,
        {
          id: agreementId,
          eventId: eventId,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getAgreementAssignees: async (
    agreementId: string,
    params: {
      Q?: string;
      Page: number;
      Size: number;
      OrderBy: number;
      SortBy?: string;
    },
  ): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/agreements/${agreementId}/assignees?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getUserAgreements: async (
    userId: string,
    params: {
      Q?: string;
      Page: number;
      Size: number;
      OrderBy: number;
      SortBy?: string;
    },
  ): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/agreements/assignments/users/${userId}?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Versions
  getAgreementVersion: async (
    agreementId: string,
    version: string,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.get(
        `/agreements/${agreementId}/versions/${version}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  downloadAgreementVersion: async (
    agreementId: string,
    version: string,
  ): Promise<Blob> => {
    try {
      const response = await api.get(
        `/agreements/${agreementId}/versions/${version}/download`,
        {
          responseType: "blob",
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get agreement version blob
  getAgreementVersionBlob: async (
    agreementId: string,
    version: string,
  ): Promise<{ blob: Blob; contentType: string; fileName?: string }> => {
    try {
      // Don't use responseType: "blob" since API returns JSON with base64
      const response = await api.get(
        `/agreements/${agreementId}/versions/${version}/blob`,
      );

      // API returns: { success: true, data: { blob: "base64string..." } }
      const jsonData = response.data;

      if (jsonData.success && jsonData.data && jsonData.data.blob) {
        const base64Data = jsonData.data.blob;

        // Decode base64 to binary
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        // Determine MIME type from response or default
        const mimeType =
          jsonData.data.contentType ||
          jsonData.data.mimeType ||
          "application/octet-stream";

        return {
          blob: new Blob([bytes], { type: mimeType }),
          contentType: mimeType,
          fileName: jsonData.data.fileName,
        };
      }

      throw new Error("Invalid response format");
    } catch (error) {
      throw error;
    }
  },
};

// Credit Cards API (Finance)
export const creditCardsAPI = {
  createCreditCard: async (request: {
    cardholderName: string;
    cardNumber: string;
    expiryMonth: number;
    expiryYear: number;
    cvv: string;
    isDefault?: boolean;
  }): Promise<ApiResponse> => {
    try {
      const response = await api.post("/finance/credit-cards", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  listCreditCards: async (params: {
    Q?: string;
    Page: number;
    Size: number;
    OrderBy: number;
    SortBy?: string;
  }): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(
        `/finance/credit-cards?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getInstallmentOptions: async (
    cardId: string,
    amount: number,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.get(
        `/finance/credit-cards/${cardId}/installment-options?amount=${amount}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteCreditCard: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.delete(`/finance/credit-cards/${id}`);
      if (response.status === 204 || response.status === 200) {
        return { success: true, message: "Kart başarıyla silindi", data: null };
      }
      return (
        response.data || {
          success: true,
          message: "Kart başarıyla silindi",
          data: null,
        }
      );
    } catch (error) {
      throw error;
    }
  },
};

// Credit Applications API (Finance)
export const creditApplicationsAPI = {
  startApplication: async (request: {
    paymentIntentId: string;
    identityNumber: string;
    phoneNumber: string;
    birthDate: string;
  }): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        "/finance/credit-applications/start",
        request,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  verifyApplication: async (request: {
    applicationId: string;
    otpCode: string;
  }): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        "/finance/credit-applications/verify",
        request,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  applyApplication: async (request: {
    applicationId: string;
    term: number;
  }): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        "/finance/credit-applications/apply",
        request,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getInstallments: async (amount: number): Promise<ApiResponse> => {
    try {
      const response = await api.get(
        `/finance/credit-applications/installments?Amount=${amount}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateStatus: async (
    paymentIntentId: string,
    status: number,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        `/finance/credit-applications/status/${paymentIntentId}/${status}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Payment Basket API (Finance)
export const paymentBasketAPI = {
  createBasket: async (preferredCurrency?: number): Promise<ApiResponse> => {
    try {
      const response = await api.post("/finance/payments/basket", {
        preferredCurrency: preferredCurrency,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getMyBasket: async (): Promise<ApiResponse> => {
    try {
      const response = await api.get("/finance/payments/basket/mine");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  addItemToBasket: async (receivableIds: string[]): Promise<ApiResponse> => {
    try {
      const response = await api.post("/finance/payments/basket/items", {
        receivableIds: receivableIds,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  removeItemFromBasket: async (basketItemId: string): Promise<ApiResponse> => {
    try {
      const response = await api.delete(
        `/finance/payments/basket/items/${basketItemId}`,
      );
      if (response.status === 204) {
        return {
          success: true,
          message: "Ürün sepetten kaldırıldı",
          data: null,
        };
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Payment Intents API (Finance)
export const paymentIntentsAPI = {
  createIntent: async (
    basketId: string,
    idempotencyKey?: string,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.post("/finance/payments/intents", {
        basketId: basketId,
        idempotencyKey: idempotencyKey,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  confirmIntent: async (
    intentId: string,
    card: {
      cardNumber: string;
      expiryMonth: string;
      expiryYear: string;
      cvv: string;
      cardHolderName: string;
      installmentCount?: number;
    },
    splited: boolean,
    amount: number,
    idempotencyKey?: string,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        `/finance/payments/intents/${intentId}/confirm`,
        {
          paymentIntentId: intentId,
          card: card,
          splited: splited,
          amount: amount,
          idempotencyKey: idempotencyKey,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  confirmSingleCardIntent: async (
    intentId: string,
    card: {
      cardNumber: string;
      expiryMonth: string;
      expiryYear: string;
      cvv: string;
      cardHolderName: string;
    },
  ): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        `/finance/payments/intents/${intentId}/confirm-single-card`,
        {
          paymentIntentId: intentId,
          card: card,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  captureIntent: async (
    intentId: string,
    amount?: number,
    idempotencyKey?: string,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        `/finance/payments/intents/${intentId}/capture`,
        {
          paymentIntentId: intentId,
          amount: amount,
          idempotencyKey: idempotencyKey,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  cancelIntent: async (
    intentId: string,
    reason?: string,
    idempotencyKey?: string,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        `/finance/payments/intents/${intentId}/cancel`,
        {
          paymentIntentId: intentId,
          reason: reason,
          idempotencyKey: idempotencyKey,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  refundIntent: async (
    intentId: string,
    amount?: number,
    reason?: string,
    idempotencyKey?: string,
  ): Promise<ApiResponse> => {
    try {
      const response = await api.post(
        `/finance/payments/intents/${intentId}/refund`,
        {
          paymentIntentId: intentId,
          amount: amount,
          reason: reason,
          idempotencyKey: idempotencyKey,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Payment Methods API (Finance)
export const paymentMethodsAPI = {
  listPaymentMethods: async (): Promise<ApiResponse> => {
    try {
      const response = await api.get("/finance/payments/methods");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createPaymentMethod: async (request: {
    provider: string;
    token: string;
    maskedPan?: string;
    expiryMonth?: number;
    expiryYear?: number;
    brand?: string;
    isDefault?: boolean;
  }): Promise<ApiResponse> => {
    try {
      const response = await api.post("/finance/payments/methods", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// FairPay API
export const fairPayAPI = {
  threedPay: async (transactionId: number): Promise<ApiResponse> => {
    try {
      const response = await api.post("/FairPay/ThreedPay", {
        transactionId: transactionId,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  threedInitialize: async (request: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/FairPay/ThreedInitialize", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  checkCard: async (request: {
    paymentSourceType: string;
    cardBin: string;
    amount: { value: number; currency: string };
    paymentOrigin?: string;
    force3DSecure?: boolean;
  }): Promise<ApiResponse> => {
    try {
      const response = await api.post("/FairPay/CheckCard", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createRefund: async (request: {
    requestReference?: string;
    financialGroupId?: string;
    amount: { value: number; currency: string };
    originalTransactionId: number;
  }): Promise<ApiResponse> => {
    try {
      const response = await api.post("/FairPay/CreateRefund", request);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Users API
export const usersAPI = {
  getCurrentUser: async (): Promise<ApiResponse> => {
    try {
      const response = await api.get("/users/me");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  checkUserStatus: async (username: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(
        `/users/check-user-status?Username=${encodeURIComponent(username)}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  listUsers: async (params: {
    Status?: number;
    UserTypes?: string[];
    Q?: string;
    Page: number;
    Size: number;
    OrderBy: number;
    SortBy?: string;
  }): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Status !== undefined)
        queryParams.append("Status", params.Status.toString());
      if (params.UserTypes && params.UserTypes.length > 0) {
        params.UserTypes.forEach((type) =>
          queryParams.append("UserTypes", type),
        );
      }
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(`/users?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getUserDetails: async (userId: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/users/${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateUser: async (
    userId: string,
    request: {
      firstName?: string;
      lastName?: string;
      userStatus?: number;
      twoFactorEnabled?: boolean;
    },
  ): Promise<ApiResponse> => {
    try {
      const response = await api.put(`/users/${userId}`, {
        userId: userId,
        ...request,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Content API
export const contentAPI = {
  getFAQ: async (params: {
    Q?: string;
    Page: number;
    Size: number;
    OrderBy: number;
    SortBy?: string;
  }): Promise<ApiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.Q) queryParams.append("Q", params.Q);
      queryParams.append("Page", params.Page.toString());
      queryParams.append("Size", params.Size.toString());
      queryParams.append("OrderBy", params.OrderBy.toString());
      if (params.SortBy) queryParams.append("SortBy", params.SortBy);

      const response = await api.get(`/content/faq?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};
export const updateUserProfile = async (data: {
  firstName: string;
  lastName: string;
  email: string;
}): Promise<ApiResponse> => {
  try {
    const response = await api.put(`/users/profile`, data);

    return response.data;
  } catch (error) {
    throw error;
  }
};


export type OrderBy = 0 | 1; 

export interface TicketListQuery {
  Q?: string;
  Page?: number;
  Size?: number;
  OrderBy?: OrderBy;
  SortBy?: string;
}

export interface CreateSupportTicketRequest {
  subject: string;
  description: string;
}

export type SupportTicketStatusId = 1 | 2 | 3 | 4;

export interface ChangeSupportTicketStatusRequest {
  ticketId: string;
  status: SupportTicketStatusId;
}

export interface SupportTicketListItem {
  ticketId: string;
  ticketNo?: string;
  subject: string;
  description?: string;

  statusId?: number;
  status?: string;

  assignedUserId?: string | null;
  assignedUser?: string | null;

  createdAt?: string;      // list
  createdDate?: string;    // detail
}
export type SupportTicketDetail = SupportTicketListItem & {
  history?: any[];
};


const SUPPORT_TICKET_BASE = "/SupportTicket";

export const supportTicketAPI = {
  create: async (data: CreateSupportTicketRequest): Promise<ApiResponse> => {
    try {
      const response = await api.post(SUPPORT_TICKET_BASE, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  listAll: async (params?: TicketListQuery): Promise<ApiResponse> => {
    try {
      const response = await api.get(SUPPORT_TICKET_BASE, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  listMy: async (params?: TicketListQuery): Promise<ApiResponse> => {
    try {
      const response = await api.get(`${SUPPORT_TICKET_BASE}/my`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getById: async (id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`${SUPPORT_TICKET_BASE}/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateStatus: async (ticketId: string, status: SupportTicketStatusId): Promise<ApiResponse> => {
    try {
      const body: ChangeSupportTicketStatusRequest = { ticketId, status };
      const response = await api.put(`${SUPPORT_TICKET_BASE}/${ticketId}/status`, body);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};


export default api;
